import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { requestJson } from '../utils/authClient.js';
import { AdminSection, ShowMore, useShowMore } from '../components/AdminNav.jsx';
import { formatBerlin } from './VeridionShared.jsx';

// The mailboxes Veridion Core keeps for its agents (IDD/projects/veridion/core): every position of
// the organisation, the hosted agents and the agents that run. A mailbox holds what reached the
// agent from people, the dashboard, other agents and its parent; hand-overs between positions travel
// through them. The Core's admin door answers per agent, so the panel reads each mailbox and joins
// them; the newest sixty messages of each mailbox are what the door returns.
const CORE = '/api/admin/veridion-core';
const REFRESH_MS = 30000;
const ACTIVE = ['building', 'starting', 'running', 'waiting', 'idle', 'paused'];
const STATES = ['queued', 'delivered', 'consumed'];

const formatTime = formatBerlin;
const textOf = (body) => {
  if (!body) return '';
  if (typeof body === 'string') return body;
  return body.text || body.command || JSON.stringify(body);
};

const VeridionMailboxesPanel = ({ t }) => {
  const o = t.admin.ops || {};
  const [boxes, setBoxes] = useState([]);
  const [loadedAt, setLoadedAt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [mailboxFilter, setMailboxFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [openMessage, setOpenMessage] = useState(null);
  const [shown, more] = useShowMore(50);

  const load = useCallback(async () => {
    setBusy(true);
    const [organisation, overview, agents] = await Promise.allSettled([
      requestJson(`${CORE}/organisation`),
      requestJson(`${CORE}/overview`),
      requestJson(`${CORE}/agents?include_finished=false`),
    ]);
    const known = [];
    if (organisation.status === 'fulfilled') {
      (organisation.value.positions || []).forEach((position) => known.push({ id: position.agentId, name: `${position.bot} · ${position.title}`, kind: 'position' }));
    }
    if (overview.status === 'fulfilled') {
      (overview.value.hosted || []).forEach((entry) => known.push({ id: entry.id, name: entry.title, kind: 'hosted' }));
    }
    if (agents.status === 'fulfilled') {
      (agents.value.items || []).filter((agent) => agent.kind === 'graph' && ACTIVE.includes(agent.status))
        .forEach((agent) => known.push({ id: agent.id, name: agent.title || agent.id, kind: 'graph' }));
    }
    const unique = known.filter((box, index) => box.id && known.findIndex((other) => other.id === box.id) === index);
    const details = await Promise.allSettled(unique.map((box) => requestJson(`${CORE}/agents/${encodeURIComponent(box.id)}`)));
    setBoxes(unique.map((box, index) => ({
      ...box,
      messages: details[index].status === 'fulfilled' ? (details[index].value.messages || []) : [],
      failed: details[index].status !== 'fulfilled',
    })));
    setError(organisation.status === 'fulfilled' || overview.status === 'fulfilled' ? '' : (organisation.reason?.code || 'unavailable'));
    setLoadedAt(new Date().toISOString());
    setBusy(false);
  }, [t]);

  useEffect(() => { load(); const timer = setInterval(load, REFRESH_MS); return () => clearInterval(timer); }, [load]);

  const names = useMemo(() => Object.fromEntries(boxes.map((box) => [box.id, box.name])), [boxes]);
  const messages = useMemo(() => boxes
    .flatMap((box) => box.messages.map((message) => ({ ...message, mailbox: box.id })))
    .filter((message) => (!mailboxFilter || message.mailbox === mailboxFilter) && (!statusFilter || message.status === statusFilter))
    .sort((left, right) => String(right.created_at).localeCompare(String(left.created_at)) || (right.id - left.id)),
  [boxes, mailboxFilter, statusFilter]);

  // A hand-over carries the sending position's agent as its sender, a person's message their address.
  const sender = (message) => names[message.sender] || message.sender || '-';
  const statusLabel = (status) => t.admin.core?.messageStatus?.[status] || status || '-';
  const statusClass = (status) => (status === 'consumed' ? 'is-ok' : status === 'queued' ? 'is-warning' : 'is-neutral');
  const kindLabel = (kind) => o.messageKinds?.[kind] || kind || '-';

  const queued = boxes.reduce((sum, box) => sum + box.messages.filter((message) => message.status === 'queued').length, 0);
  return (
    <>
      <AdminSection
        id="mailboxes"
        title={o.mailboxesTitle}
        subtitle={o.mailboxesSubtitle}
        count={boxes.length}
        state={`${statusLabel('queued')}: ${queued}`}
        tone={queued ? 'warning' : boxes.length ? 'ok' : ''}
        actions={(
          <div className="admin-card-actions">
            <span className="text-sm text-text-secondary">{o.readAt}: {formatTime(loadedAt)}</span>
            <button type="button" className="homepage-button-secondary" disabled={busy} onClick={load}>{o.refresh}</button>
          </div>
        )}
      >
        {error ? <div className="homepage-muted-panel p-4 text-sm text-error">{t.admin.core?.unavailable} ({error})</div> : null}
        <div className="admin-table-shell"><div className="admin-table-scroll">
          <table className="admin-table admin-fixed-table">
            <colgroup><col style={{ width: '34%' }} /><col style={{ width: '14%' }} /><col style={{ width: '10%' }} /><col style={{ width: '10%' }} /><col style={{ width: '10%' }} /><col style={{ width: '22%' }} /></colgroup>
            <thead>
              <tr>
                <th>{o.mailbox}</th><th>{o.mailboxKind}</th>
                {STATES.map((state) => <th key={state}>{statusLabel(state)}</th>)}
                <th>{o.lastMessage}</th>
              </tr>
            </thead>
            <tbody>
              {boxes.map((box) => {
                const count = (state) => box.messages.filter((message) => message.status === state).length;
                const last = box.messages.reduce((newest, message) => (String(message.created_at) > String(newest) ? message.created_at : newest), '');
                return (
                  <tr key={box.id} className={`cursor-pointer ${mailboxFilter === box.id ? 'is-selected' : ''}`} onClick={() => setMailboxFilter(mailboxFilter === box.id ? '' : box.id)}>
                    <td>
                      <div className="font-semibold text-text-primary admin-cell-clip" title={box.name}>{box.name}</div>
                      <div className="text-xs text-text-muted admin-cell-clip" title={box.id}>{box.id}</div>
                    </td>
                    <td>{o.mailboxKinds?.[box.kind] || box.kind}</td>
                    {STATES.map((state) => (
                      <td key={state}>{count(state) ? <span className={`admin-status-pill ${statusClass(state)}`}>{count(state)}</span> : '0'}</td>
                    ))}
                    <td>{box.failed ? <span className="text-error">{t.admin.core?.failed}</span> : formatTime(last)}</td>
                  </tr>
                );
              })}
              {!boxes.length ? <tr><td colSpan="6">{busy ? t.admin.core?.loading : o.noMailboxes}</td></tr> : null}
            </tbody>
          </table>
        </div></div>
      </AdminSection>

      <AdminSection id="mailbox-messages" title={o.messagesTitle} subtitle={o.messagesSubtitle} count={messages.length}>
        <div className="admin-filter-row">
          <select className="form-input" value={mailboxFilter} onChange={(event) => setMailboxFilter(event.target.value)}>
            <option value="">{o.allMailboxes}</option>
            {boxes.map((box) => <option key={box.id} value={box.id}>{box.name}</option>)}
          </select>
          <select className="form-input" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="">{o.allStates}</option>
            {STATES.map((state) => <option key={state} value={state}>{statusLabel(state)}</option>)}
          </select>
          <span className="admin-user-count">{messages.length}</span>
        </div>
        <div className="admin-table-shell"><div className="admin-table-scroll">
          <table className="admin-table admin-fixed-table">
            <colgroup><col style={{ width: '14%' }} /><col style={{ width: '18%' }} /><col style={{ width: '18%' }} /><col style={{ width: '10%' }} /><col style={{ width: '10%' }} /><col style={{ width: '30%' }} /></colgroup>
            <thead>
              <tr><th>{o.time}</th><th>{o.from}</th><th>{o.to}</th><th>{o.messageKind}</th><th>{t.admin.core?.statusLabel}</th><th>{o.content}</th></tr>
            </thead>
            <tbody>
              {messages.slice(0, shown).map((message) => {
                const key = `${message.mailbox}-${message.id}`;
                const text = textOf(message.body);
                return (
                  <tr key={key} className="cursor-pointer" onClick={() => setOpenMessage(openMessage === key ? null : key)}>
                    <td>{formatTime(message.created_at)}</td>
                    <td className="admin-cell-clip" title={sender(message)}>{sender(message)}{message.urgent ? ' !' : ''}</td>
                    <td className="admin-cell-clip" title={names[message.mailbox]}>{names[message.mailbox]}</td>
                    <td>{kindLabel(message.kind)}</td>
                    <td><span className={`admin-status-pill ${statusClass(message.status)}`}>{statusLabel(message.status)}</span></td>
                    <td className="text-xs">
                      {openMessage === key ? <div style={{ whiteSpace: 'pre-wrap' }}>{text}{message.reply ? `\n\n${o.reply}: ${textOf(message.reply)}` : ''}</div> : <div className="admin-cell-clip" title={text}>{text}</div>}
                    </td>
                  </tr>
                );
              })}
              {!messages.length ? <tr><td colSpan="6">{o.noMessages}</td></tr> : null}
            </tbody>
          </table>
        </div></div>
        <ShowMore shown={shown} total={messages.length} onMore={more} />
      </AdminSection>
    </>
  );
};

export default VeridionMailboxesPanel;
